import { useCentralizedMe } from '@crehana/auth';
import { useSyncExternalStore } from 'react';

import { useForcedUi } from '@/shared/hooks/use-forced-ui';

import type { TRequestComment, TRequestResponder } from '../../types';
import {
  addForcedRequestComment,
  getForcedRequestDetailVersion,
  resolveForcedRequestDetail,
  subscribeToForcedRequestDetail,
} from './forced-request-detail';

type TUseRequestDetailResult = {
  respondedBy: TRequestResponder | null;
  comments: TRequestComment[];
  loading: boolean;
  error: boolean;
  submitting: boolean;
  canComment: boolean;
  addComment: (body: string) => void;
  refetch: () => void;
};

// TIM-557 (the detail + comments backend) does not exist yet — verified
// against the qa gateway, 2026-09-20. Every branch below is mock, behind
// `?force=solicitud-detalle[:variant]`. `useSyncExternalStore` re-renders
// this hook when `addComment` appends a comment to the session store (see
// forced-request-detail.ts).
export const useRequestDetail = (
  requestId: string | null,
): TUseRequestDetailResult => {
  const { me } = useCentralizedMe();

  useSyncExternalStore(
    subscribeToForcedRequestDetail,
    getForcedRequestDetailVersion,
    getForcedRequestDetailVersion,
  );

  const forceVariant = useForcedUi('solicitud-detalle');
  const forced = requestId
    ? resolveForcedRequestDetail(forceVariant, requestId)
    : undefined;
  // TIM-557 has no write endpoint yet, so a comment can only reach the
  // session store the mock read path serves — the drawer must never let a
  // collaborator type into a composer whose input a real read never sees.
  const canComment = forced !== undefined;

  const addComment = (body: string): void => {
    if (!requestId || !canComment) {
      return;
    }
    addForcedRequestComment(requestId, {
      id: `forced-comment-${crypto.randomUUID()}`,
      comment: body,
      created_at: new Date().toISOString(),
      user: {
        id: me.user.id,
        full_name: me.user.fullName,
        profile_picture: { url: me.user.avatarUrl ?? null },
      },
    });
  };

  if (forced !== undefined) {
    return {
      respondedBy: forced.respondedBy,
      comments: forced.comments,
      loading: forced.loading,
      error: forced.error,
      submitting: false,
      canComment,
      addComment,
      refetch: () => undefined,
    };
  }

  return {
    respondedBy: null,
    comments: [],
    loading: false,
    error: false,
    submitting: false,
    canComment,
    addComment,
    refetch: () => undefined,
  };
};
